import { useState, useRef, useCallback, useEffect } from 'react'

// Audio utilities (inlined from components/src/utils/audio.ts)

const MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
]

const MAX_RECORDING_DURATION = 240

const AudioErrorMessages = {
  BROWSER_NOT_SUPPORTED:
    'Your browser does not support audio recording. Please use Chrome, Firefox, or Edge.',
  MICROPHONE_IN_USE:
    'Microphone is being used by another application. Please close other apps and try again.',
  NO_MICROPHONE:
    'No microphone detected. Please connect a microphone and try again.',
  PERMISSION_DENIED:
    'Microphone access denied. Please allow microphone access in your browser settings.',
  RECORDING_FAILED: 'Failed to record audio. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
}

const checkBrowserSupport = () => {
  return !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof window.MediaRecorder !== 'undefined'
  )
}

const getSupportedMimeType = () => {
  if (typeof window.MediaRecorder === 'undefined') {
    return null
  }
  for (let _mimeType of MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(_mimeType)) {
      return _mimeType
    }
  }
  return null
}

const requestMicrophoneAccess = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        autoGainControl: true,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 48000,
        sampleSize: 16,
      },
    })
    return stream
  } catch (error) {
    if (error instanceof Error) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        throw new Error(
          'Microphone access denied. Please allow microphone access to record audio.',
        )
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        throw new Error(
          'No microphone found. Please connect a microphone to record audio.',
        )
      } else if (
        error.name === 'NotReadableError' ||
        error.name === 'TrackStartError'
      ) {
        throw new Error('Microphone is already in use by another application.')
      }
    }
    throw new Error(
      'Failed to access microphone. Please check your browser settings.',
    )
  }
}

const createAudioBlob = (chunks, mimeType) => {
  return new Blob(chunks, { type: mimeType })
}

const getRecordingErrorMessage = (error) => {
  if (
    error.name === 'NotAllowedError' ||
    error.name === 'PermissionDeniedError'
  ) {
    return AudioErrorMessages.PERMISSION_DENIED
  } else if (
    error.name === 'NotFoundError' ||
    error.name === 'DevicesNotFoundError'
  ) {
    return AudioErrorMessages.NO_MICROPHONE
  } else if (
    error.name === 'NotReadableError' ||
    error.name === 'TrackStartError'
  ) {
    return AudioErrorMessages.MICROPHONE_IN_USE
  } else if (error.message && error.message.includes('not supported')) {
    return AudioErrorMessages.BROWSER_NOT_SUPPORTED
  }
  return AudioErrorMessages.UNKNOWN_ERROR
}

export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Hook

const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [remainingTime, setRemainingTime] = useState(MAX_RECORDING_DURATION)
  const [audioBlob, setAudioBlob] = useState(null)
  const [audioUrl, setAudioUrl] = useState(null)
  const [error, setError] = useState(null)
  const [isSupported, setIsSupported] = useState(true)

  const mediaRecorderRef = useRef(null)
  const streamRef = useRef(null)
  const chunksRef = useRef([])
  const timerRef = useRef(null)
  const startTimeRef = useRef(0)
  const stopRecordingPromiseRef = useRef(null)

  const stopRecording = useCallback(() => {
    return new Promise((resolve, reject) => {
      stopRecordingPromiseRef.current = { reject, resolve }

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop()
      } else {
        stopRecordingPromiseRef.current = null
        resolve(null)
      }

      setIsRecording(false)
      setIsPaused(false)

      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    })
  }, [])

  const resetRecording = useCallback(() => {
    if (isRecording) {
      stopRecording()
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
    }

    setAudioBlob(null)
    setAudioUrl(null)
    setRecordingTime(0)
    setRemainingTime(MAX_RECORDING_DURATION)
    setError(null)
    chunksRef.current = []
  }, [isRecording, audioUrl, stopRecording])

  useEffect(() => {
    setIsSupported(checkBrowserSupport())
  }, [])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    }
  }, [audioUrl])

  useEffect(() => {
    if (isRecording && !isPaused) {
      startTimeRef.current = Date.now() - recordingTime * 1000
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        const remaining = Math.max(0, MAX_RECORDING_DURATION - elapsed)

        setRecordingTime(elapsed)
        setRemainingTime(remaining)

        if (remaining <= 0) {
          stopRecording()
        }
      }, 100)
    } else if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isRecording, isPaused, recordingTime, stopRecording])

  const startRecording = useCallback(async () => {
    try {
      setError(null)

      if (!isSupported) {
        throw new Error(AudioErrorMessages.BROWSER_NOT_SUPPORTED)
      }

      const mimeType = getSupportedMimeType()
      if (!mimeType) {
        throw new Error('No supported audio format found')
      }

      const stream = await requestMicrophoneAccess()
      streamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream, {
        audioBitsPerSecond: 192000,
        mimeType,
      })

      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = createAudioBlob(chunksRef.current, mimeType)
        setAudioBlob(blob)

        const url = URL.createObjectURL(blob)
        setAudioUrl(url)

        if (stopRecordingPromiseRef.current) {
          stopRecordingPromiseRef.current.resolve(blob)
          stopRecordingPromiseRef.current = null
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
          streamRef.current = null
        }
      }

      mediaRecorder.onerror = () => {
        setError(AudioErrorMessages.RECORDING_FAILED)
        if (stopRecordingPromiseRef.current) {
          stopRecordingPromiseRef.current.reject(
            new Error(AudioErrorMessages.RECORDING_FAILED),
          )
          stopRecordingPromiseRef.current = null
        }
        stopRecording()
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)
      setRemainingTime(MAX_RECORDING_DURATION)
      setAudioBlob(null)
      setAudioUrl(null)
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? getRecordingErrorMessage(err)
          : AudioErrorMessages.UNKNOWN_ERROR
      setError(errorMessage)
    }
  }, [isSupported, stopRecording])

  return {
    audioBlob,
    audioUrl,
    error,
    isPaused,
    isRecording,
    isSupported,
    recordingTime,
    remainingTime,
    resetRecording,
    startRecording,
    stopRecording,
  }
}

export default useAudioRecorder
